"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMobile } from "@/hooks/use-mobile";
import { DockSheet } from "@/components/ui/dock-sheet";
import { SearchablePickerList } from "@/components/ui/searchable-picker-list";
import { SHEET_Z_PICKER } from "@/lib/sheet-styles";
import {
  SEARCHABLE_DROPDOWN_MUTED_TEXT_CLASS,
  SEARCHABLE_DROPDOWN_TRIGGER_BASE,
  searchableDropdownPanelClass,
  searchableDropdownTriggerClass,
} from "@/lib/dropdown-styles";

import type { SearchableOption } from "@/lib/dropdown-types";

export type MultiOption = SearchableOption;

interface SearchableMultiDropdownProps {
  options: MultiOption[];
  label: string;
  selectedCodes: string[];
  onChange: (codes: string[]) => void;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  searchInputClassName?: string;
  optionClassName?: string;
  placeholder?: string;
  showCode?: boolean;
  showFlag?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  loading?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  sheetTitle?: string;
  onSearchQueryChange?: (query: string) => void;
  searchDebounceMs?: number;
  mobileSheet?: boolean;
  showBulkActions?: boolean;
  selectAllLabel?: string;
  deselectAllLabel?: string;
  selectAllExcludeCodes?: string[];
  selectedCountLabel?: (count: number) => string;
}

const SearchableMultiDropdown: React.FC<SearchableMultiDropdownProps> = ({
  options,
  label,
  selectedCodes,
  onChange,
  className,
  triggerClassName,
  contentClassName,
  searchInputClassName,
  optionClassName,
  placeholder = "Select...",
  showCode = false,
  showFlag = true,
  searchPlaceholder = "Search...",
  emptyMessage = "No results found",
  loading = false,
  onOpenChange,
  sheetTitle,
  onSearchQueryChange,
  searchDebounceMs = 300,
  mobileSheet = true,
  showBulkActions,
  selectAllLabel = "Select all",
  deselectAllLabel = "Deselect all",
  selectAllExcludeCodes = [],
  selectedCountLabel,
}) => {
  const isMobile = useMobile();
  const useMobileSheet = mobileSheet && isMobile;

  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (useMobileSheet) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        onOpenChange?.(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onOpenChange, useMobileSheet]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    onOpenChange?.(false);
    setQuery("");
    onSearchQueryChange?.("");
  }, [onOpenChange, onSearchQueryChange]);

  const handleQueryChange = useCallback(
    (value: string) => {
      setQuery(value);
      if (!onSearchQueryChange) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onSearchQueryChange(value);
      }, searchDebounceMs);
    },
    [onSearchQueryChange, searchDebounceMs],
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleToggle = useCallback(
    (option: MultiOption) => {
      if (selectedCodes.includes(option.code)) {
        onChange(selectedCodes.filter((code) => code !== option.code));
        return;
      }
      onChange([...selectedCodes, option.code]);
    },
    [onChange, selectedCodes],
  );

  const openDropdown = () => {
    setIsOpen(true);
    onOpenChange?.(true);
  };

  const toggleDropdown = () => {
    if (isOpen) {
      closeDropdown();
    } else {
      openDropdown();
    }
  };

  const triggerLabel =
    selectedCodes.length > 0
      ? selectedCountLabel
        ? selectedCountLabel(selectedCodes.length)
        : `${selectedCodes.length} selected`
      : null;

  const trigger = (
    <div
      className={cn(
        triggerClassName,
        !triggerClassName && [SEARCHABLE_DROPDOWN_TRIGGER_BASE, searchableDropdownTriggerClass()],
      )}
      onClick={toggleDropdown}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggleDropdown();
        }
      }}
    >
      <div className="flex-1 overflow-hidden">
        {triggerLabel ? (
          <span className="truncate">{triggerLabel}</span>
        ) : (
          <span className={SEARCHABLE_DROPDOWN_MUTED_TEXT_CLASS}>{placeholder}</span>
        )}
      </div>
      <ChevronDown
        className={cn(
          "h-4 w-4 transition-transform duration-200 shrink-0",
          SEARCHABLE_DROPDOWN_MUTED_TEXT_CLASS,
          isOpen ? "rotate-180" : "",
        )}
      />
    </div>
  );

  const pickerProps = {
    options,
    label,
    query,
    onQueryChange: handleQueryChange,
    onSelect: () => {},
    showFlag,
    showCode,
    loading,
    emptyMessage,
    searchPlaceholder,
    optionClassName,
    searchInputClassName,
    clientFilter: !onSearchQueryChange,
    multiSelect: true,
    selectedCodes,
    onToggleSelect: handleToggle,
    onBulkChange: onChange,
    showBulkActions,
    selectAllLabel,
    deselectAllLabel,
    selectAllExcludeCodes,
  };

  if (useMobileSheet) {
    return (
      <div className={cn("relative w-full", className)} ref={inputRef}>
        {trigger}
        <DockSheet
          open={isOpen}
          onOpenChange={(open) => {
            if (open) {
              setIsOpen(true);
              onOpenChange?.(true);
            } else {
              closeDropdown();
            }
          }}
          title={sheetTitle ?? placeholder}
          zIndex={SHEET_Z_PICKER}
          scrollManagedByChild
          belowTitle={
            selectedCodes.length > 0 ? (
              <p className={cn("text-sm", SEARCHABLE_DROPDOWN_MUTED_TEXT_CLASS)}>
                {selectedCountLabel
                  ? selectedCountLabel(selectedCodes.length)
                  : `${selectedCodes.length} selected`}
              </p>
            ) : null
          }
          contentClassName="flex min-h-0 flex-col overflow-hidden pt-0"
        >
          <div className="-mx-4 flex min-h-0 flex-1 flex-col">
            <SearchablePickerList {...pickerProps} variant="sheet" />
          </div>
        </DockSheet>
      </div>
    );
  }

  return (
    <div className={cn("relative w-full", className)} ref={inputRef}>
      {trigger}

      <div
        className={cn(
          "absolute z-30 top-full mt-2 w-full overflow-hidden shadow-2xl transition-all duration-300 ease-in-out",
          contentClassName || searchableDropdownPanelClass(),
          isOpen ? "opacity-100 translate-y-0 visible" : "opacity-0 -translate-y-4 invisible",
        )}
        style={{ maxHeight: "min(320px, 50vh)" }}
      >
        <SearchablePickerList
          {...pickerProps}
          autoFocusSearch={isOpen}
          listStyle={{ maxHeight: "min(280px, 46vh)" }}
        />
      </div>
    </div>
  );
};

export default SearchableMultiDropdown;
